import React from "react";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

import { Button } from "../../../../components/shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";

import ColorInput from "./ColorInput";
import styles from "../stylesDynamicBonuses.module.css";

type ColorListInputProps = {
  value?: string[];
  onChange?: (value: string[]) => void;
  /** Placeholder for each stop's hex field (e.g. `#FC4233`). */
  placeholder?: string;
  /** Label for the add-stop control. Defaults to `"Add color"`. */
  addLabel?: string;
  /**
   * When true, render Add as an orange text action (Figma Customize Banner)
   * instead of the default outline button.
   */
  textAction?: boolean;
  /** Hide the gradient strip when a live preview already shows the colors. */
  hidePreview?: boolean;
  /** Render color stops and add control on one row (compact freeform banner UI). */
  inline?: boolean;
};

const DEFAULT_STOP = "#FFFFFF";

/**
 * Multi-stop gradient editor: one swatch + hex row per color, with add/remove.
 * Supports a single solid color or multiple gradient stops.
 */
const ColorListInput: React.FC<ColorListInputProps> = ({
  value,
  onChange,
  placeholder = DEFAULT_STOP,
  addLabel = AppStrings.PAM_DYNAMIC_BONUS_BANNER_ADD_COLOR,
  textAction = false,
  hidePreview = false,
  inline = false,
}) => {
  const colors = value?.length ? value : [""];

  const commit = (next: string[]) => {
    onChange?.(next);
  };

  const updateAt = (index: number, color: string) => {
    const next = [...colors];
    next[index] = color;
    commit(next);
  };

  const removeAt = (index: number) => {
    if (colors.length <= 1) {
      commit([""]);
      return;
    }
    commit(colors.filter((_, i) => i !== index));
  };

  const addStop = () => {
    const last = colors[colors.length - 1]?.trim();
    commit([...colors, last || DEFAULT_STOP]);
  };

  const previewStops = colors.filter((color) =>
    /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/.test(color.trim()),
  );

  return (
    <div
      className={[
        styles.colorListInput,
        inline ? styles.colorListInputInline : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-testid="color-list-input"
      data-inline={inline ? "true" : undefined}
    >
      {!hidePreview && previewStops.length > 0 && (
        <div
          className={styles.colorListPreview}
          data-testid="color-list-preview"
          style={{
            background:
              previewStops.length === 1
                ? previewStops[0]
                : `linear-gradient(90deg, ${previewStops.join(", ")})`,
          }}
          aria-hidden
        />
      )}
      {colors.map((color, index) => (
        <div key={index} className={styles.colorListStop}>
          <ColorInput
            value={color}
            onChange={(next) => updateAt(index, next)}
            placeholder={placeholder}
          />
          <Button
            htmlType="button"
            intent="neutral"
            variant="ghost"
            iconLeft={<DeleteOutlined />}
            aria-label={formatString(
              AppStrings.PAM_DYNAMIC_BONUS_BANNER_REMOVE_COLOR,
              { index: String(index + 1) },
            )}
            onClick={() => removeAt(index)}
            disabled={colors.length <= 1 && !color.trim()}
            style={{ flexShrink: 0 }}
          />
        </div>
      ))}
      <Button
        htmlType="button"
        intent="neutral"
        variant={textAction ? "ghost" : "outline"}
        iconLeft={<PlusOutlined />}
        onClick={addStop}
        className={textAction ? styles.customizeTextAction : undefined}
      >
        {addLabel}
      </Button>
    </div>
  );
};

export default ColorListInput;
